/**
 * ErrorBoundary (DC-1371)
 *
 * Generic, reusable React Error Boundary for catching component crashes.
 * Displays a configurable fallback UI with retry and navigation actions.
 *
 * Usage:
 *   <ErrorBoundary level="route" label="Journey Navigator">
 *     <JourneyNavigator />
 *   </ErrorBoundary>
 *
 * Levels:
 *   - "app"     — Top-level fallback, full-page error with reload
 *   - "route"   — Route-level, shows error card with back + retry
 *   - "section" — Section-level, compact inline error with retry
 */

import React from 'react';

// ============================================================================
// TYPES
// ============================================================================

type ErrorLevel = 'app' | 'route' | 'section';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Controls fallback UI size and actions. Default: 'route' */
  level?: ErrorLevel;
  /** Human-readable label shown in error message (e.g. "Journey Navigator") */
  label?: string;
  /** Custom fallback component. Overrides default fallback when provided. */
  fallback?: React.ReactNode;
  /** Callback fired when an error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// ============================================================================
// DEFAULT FALLBACK UI
// ============================================================================

interface ErrorFallbackProps {
  error: Error | null;
  level: ErrorLevel;
  label?: string;
  onRetry: () => void;
}

function ErrorFallback({ error, level, label, onRetry }: ErrorFallbackProps): React.ReactElement {
  const title = label
    ? `Something went wrong in ${label}`
    : 'Something went wrong';

  if (level === 'section') {
    return (
      <div style={{
        padding: '1rem',
        margin: '0.5rem 0',
        background: 'var(--error-bg, #fef2f2)',
        border: '1px solid var(--error, #ef4444)',
        borderRadius: '6px',
        fontSize: '0.875rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span>This section encountered an error.</span>
          <button
            onClick={onRetry}
            style={{
              padding: '0.25rem 0.75rem',
              background: 'var(--primary, #3b82f6)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.8rem',
            }}
          >
            Retry
          </button>
        </div>
        {error && (
          <details style={{ color: 'var(--muted, #6b7280)' }}>
            <summary style={{ cursor: 'pointer' }}>Details</summary>
            <pre style={{ fontSize: '0.75rem', marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>
              {error.message}
            </pre>
          </details>
        )}
      </div>
    );
  }

  // 'route' and 'app' level
  const isApp = level === 'app';

  return (
    <div style={{
      maxWidth: '600px',
      margin: isApp ? '8rem auto' : '4rem auto',
      padding: '2rem',
      background: 'var(--pane, #ffffff)',
      border: '2px solid var(--error, #ef4444)',
      borderRadius: '8px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
        {isApp ? '⚠️' : '🔧'}
      </div>
      <h2 style={{ color: 'var(--error, #dc2626)', marginBottom: '0.75rem' }}>
        {isApp ? 'Application Error' : title}
      </h2>
      <p style={{ color: 'var(--text, #1f2937)', marginBottom: '1.5rem' }}>
        {isApp
          ? 'The application encountered an unexpected error. Please reload the page.'
          : 'This view could not be rendered. You can retry or navigate back.'}
      </p>
      {error && (
        <details style={{
          textAlign: 'left',
          margin: '1.5rem 0',
          padding: '1rem',
          background: 'var(--canvas-bg, #fafbfc)',
          border: '1px solid var(--line, #d1d5db)',
          borderRadius: '4px',
        }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, userSelect: 'none' }}>
            Technical Details
          </summary>
          <pre style={{
            marginTop: '0.75rem',
            fontSize: '0.8rem',
            color: 'var(--muted, #6b7280)',
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
          }}>
            {error.name}: {error.message}
            {error.stack && `\n\n${error.stack}`}
          </pre>
        </details>
      )}
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
        {!isApp && (
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '0.625rem 1.25rem',
              background: 'var(--secondary, #6b7280)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '0.9rem',
              cursor: 'pointer',
            }}
          >
            Go Back
          </button>
        )}
        <button
          onClick={isApp ? () => window.location.reload() : onRetry}
          style={{
            padding: '0.625rem 1.25rem',
            background: 'var(--primary, #3b82f6)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '0.9rem',
            cursor: 'pointer',
          }}
        >
          {isApp ? 'Reload Page' : 'Retry'}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// ERROR BOUNDARY CLASS
// ============================================================================

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const level = this.props.level || 'route';
    const label = this.props.label || 'unknown';

    console.error(`[ErrorBoundary:${level}] ${label} crashed:`, error, errorInfo);

    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          level={this.props.level || 'route'}
          label={this.props.label}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}
